/**
 * DE DÓNDE SALIÓ EL PRECIO DE UN INSUMO — la regla, en un solo lugar.
 *
 * Por qué existe. Cuando la inteligencia artificial arma el análisis de precio de un
 * concepto, cada insumo de la lista consigue su precio por un camino distinto, y los
 * caminos NO valen lo mismo a la hora de defender el presupuesto ante un cliente:
 *
 *  · del catálogo del cliente     → es su propia lista de precios: se defiende solo.
 *  · de comercios identificados   → se puede decir en qué comercios se vio ese precio.
 *  · sin ningún comercio detrás    → el precio salió del conocimiento del modelo. Puede
 *    ser razonable y hasta certero, pero NO hay nada que mostrarle a quien pregunte de
 *    dónde salió. Es el caso que hay que poder ver de un golpe.
 *
 * Hasta ahora esta diferencia solo existía en la pantalla interna de Ecosoft; quien firma
 * el presupuesto veía los tres casos exactamente iguales.
 *
 * NO hay dato nuevo ni umbral nuevo acá. El renglón del análisis ya trae todo:
 *  · `fuente` la escribe el resolver de precios (`catalogo` / `mercado` / `missing` /
 *    `auxiliar` / `costo_horario` / `manual`).
 *  · `fuentes_mercado` es la lista de comercios que respaldan un precio de mercado, tal
 *    como quedó guardada en el renglón. Vacía (o ausente, en análisis viejos) significa
 *    que no hay comercio que mostrar — que es exactamente el dato que interesa.
 *  Esta función solo los lee y los traduce a los casos de arriba.
 */

/** Los caminos por los que un insumo consigue su precio, ya traducidos. */
export type OrigenPrecio =
  /** Salió del catálogo de referencia del cliente. */
  | 'catalogo'
  /** Precio de mercado con comercios identificados que se pueden mostrar. */
  | 'mercado'
  /** Precio de mercado SIN ningún comercio detrás: lo estimó la inteligencia artificial. */
  | 'mercado_estimado'
  /** Lo escribió una persona a mano en el análisis. */
  | 'a_mano'
  /** Recurso compuesto (auxiliar): no tiene precio propio, se desglosa en sus insumos. */
  | 'compuesto'
  /** No se le consiguió precio por ningún camino. */
  | 'sin_precio';

/** Lo mínimo que hay que saber de un renglón de insumo para decir de dónde salió su precio. */
export interface RenglonDeInsumo {
  /** Cómo lo resolvió el motor de precios. Ausente en análisis muy viejos. */
  fuente?: string | null;
  /** Precio que quedó en el renglón. `null` = no se consiguió. */
  precio_unitario?: number | null;
  /** Comercios que respaldan un precio de mercado. Vacía/ausente = no hay ninguno. */
  fuentes_mercado?: string[] | null;
}

/**
 * De dónde salió el precio de ESTE insumo.
 *
 * El orden importa: primero lo que el renglón declara de sí mismo (compuesto, catálogo,
 * a mano), después la falta de precio, y al final la única pregunta que separa los dos
 * casos de mercado — ¿hay algún comercio que mostrar?
 */
export function origenDelPrecio(r: RenglonDeInsumo): OrigenPrecio {
  const fuente = (r.fuente ?? '').trim();
  if (fuente === 'auxiliar') return 'compuesto';
  if (fuente === 'catalogo') return 'catalogo';
  if (fuente === 'manual') return 'a_mano';
  // Sin precio no hay origen que discutir. Cubre `fuente: 'missing'` y también los
  // renglones de análisis viejos que quedaron sin `fuente` y sin número.
  if (fuente === 'missing' || r.precio_unitario == null) return 'sin_precio';
  // Queda un precio de mercado (`mercado`, `costo_horario` o una fuente que todavía no
  // conocemos pero trajo número). Lo único que decide es si hay comercios que mostrar:
  // sin ellos, quien cotiza no tiene con qué respaldar el número.
  return comerciosDelRenglon(r).length > 0 ? 'mercado' : 'mercado_estimado';
}

/**
 * Los comercios que respaldan el precio, listos para mostrar. Devuelve `[]` cuando no hay
 * ninguno, sin distinguir "la lista quedó vacía" de "el análisis es viejo y no la guardó":
 * para quien tiene que defender el número las dos cosas significan lo mismo, y prometer esa
 * diferencia en pantalla solo agregaría ruido. La distinción fina sigue viva en la pantalla
 * interna de Ecosoft, que es donde sirve.
 */
export function comerciosDelRenglon(r: RenglonDeInsumo): string[] {
  if (!Array.isArray(r.fuentes_mercado)) return [];
  return r.fuentes_mercado.map((c) => String(c ?? '').trim()).filter((c) => c.length > 0);
}

/** Cuántos insumos del análisis salieron por cada camino. */
export interface ConteoOrigen {
  /** Insumos con precio propio (los compuestos no cuentan: no tienen precio propio). */
  total: number;
  catalogo: number;
  mercado: number;
  mercadoEstimado: number;
  aMano: number;
  sinPrecio: number;
}

/**
 * Recuento de los insumos de un análisis por origen de su precio. Es DESCRIPTIVO: cuenta lo
 * que se está mostrando en la lista, nada más. El veredicto de si el análisis está bien
 * documentado NO se calcula acá — sale del semáforo que ya guarda el análisis, con los
 * umbrales de la calibración de Ecosoft.
 */
export function contarOrigenes(renglones: RenglonDeInsumo[]): ConteoOrigen {
  return contarOrigenesClasificados(renglones.map(origenDelPrecio));
}

/**
 * El mismo recuento cuando los orígenes ya están clasificados. Lo usa la pantalla, que
 * clasifica una vez por renglón para pintarlo y no tiene por qué volver a hacerlo para
 * contar.
 */
export function contarOrigenesClasificados(origenes: OrigenPrecio[]): ConteoOrigen {
  const c: ConteoOrigen = { total: 0, catalogo: 0, mercado: 0, mercadoEstimado: 0, aMano: 0, sinPrecio: 0 };
  for (const o of origenes) {
    switch (o) {
      case 'compuesto': continue;
      case 'catalogo': c.catalogo++; break;
      case 'mercado': c.mercado++; break;
      case 'mercado_estimado': c.mercadoEstimado++; break;
      case 'a_mano': c.aMano++; break;
      case 'sin_precio': c.sinPrecio++; break;
    }
    c.total++;
  }
  return c;
}
